import { useEffect, useRef, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { useTranslate } from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";

import { formatFinancialError } from "../nfe";
import { financialQueryKey, patchClientsByCategoryService } from "../services";
import { ClientsByCategoryRow, FinancialPage, FinancialRow } from "../types";

interface ClientsCountCellProps {
  row: ClientsByCategoryRow;
  listParams: Record<string, string | number>;
  writeParams: Record<string, string | number>;
}

function parseCount(value: string) {
  if (!/^\d+$/.test(value)) return null;
  return Number(value);
}

export function ClientsCountCell({ row, listParams, writeParams }: ClientsCountCellProps) {
  const translate = useTranslate();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(String(row.clients_count ?? 0));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  useEffect(() => {
    setDraft(String(row.clients_count ?? 0));
    setError("");
  }, [row.level_id, row.clients_count, row.clients_count_edited]);

  const save = async (clientsCount: number | null) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setError("");
    const previous = row.clients_count;

    try {
      const updated = await patchClientsByCategoryService(writeParams, {
        level_id: row.level_id,
        clients_count: clientsCount,
      });
      queryClient.setQueryData<FinancialPage<FinancialRow>>(
        [financialQueryKey, "clients_by_category", listParams],
        (current) => {
          if (!current) return current;
          return {
            ...current,
            results: current.results.map((item) => {
              if (!("level_id" in item) || item.level_id !== updated.level_id) return item;
              return { ...item, ...updated };
            }),
          };
        }
      );
    } catch (caught) {
      setDraft(String(previous ?? 0));
      setError(
        formatFinancialError(caught, translate("financial_admin.messages.clients_count_save_failed"))
      );
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const commitDraft = () => {
    if (savingRef.current) return;
    const parsed = parseCount(draft);
    if (parsed === null) {
      setDraft(String(row.clients_count ?? 0));
      return;
    }
    if (parsed === row.clients_count) return;
    void save(parsed);
  };

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 0.5, py: 0.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "nowrap", minWidth: 0 }}>
        <TextField
          value={draft}
          size="small"
          type="text"
          disabled={saving}
          error={!!error}
          onChange={(event) => {
            setDraft(event.target.value.replace(/\D/g, ""));
            setError("");
          }}
          onBlur={commitDraft}
          onKeyDown={(event) => {
            event.stopPropagation();
            if (event.key === "Enter") {
              event.preventDefault();
              commitDraft();
            }
          }}
          slotProps={{
            htmlInput: {
              inputMode: "numeric",
              "aria-label": translate("financial_admin.fields.clients_count"),
            },
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            m: 0,
            "& .MuiOutlinedInput-root": { height: 40 },
            "& .MuiOutlinedInput-input": { py: 0, px: 1 },
          }}
        />
        <Button
          size="small"
          variant="outlined"
          disabled={saving}
          onMouseDown={(event) => event.preventDefault()}
          onClick={commitDraft}
          sx={{ height: 40, flexShrink: 0, px: 1.25, whiteSpace: "nowrap" }}
        >
          {translate("financial_admin.actions.save")}
        </Button>
      </Box>
      {row.clients_count_edited && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="caption" color="text.secondary">
            {translate("financial_admin.messages.clients_count_edited")}
            {row.calculated_clients_count != null && (
              <>
                {" · "}
                {translate("financial_admin.messages.calculated_clients_count", {
                  count: row.calculated_clients_count,
                })}
              </>
            )}
          </Typography>
          <Button
            size="small"
            disabled={saving}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => void save(null)}
          >
            {translate("financial_admin.actions.restore_calculated")}
          </Button>
        </Box>
      )}
      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
    </Box>
  );
}
